import {
   DataTypeWithInvalid,
   DateRange,
   formatDateYearLong, getEnvironment,
   InvalidValue,
   parseDate
} from '@unifocus/js-utils';
import React, {
   CSSProperties,
   FocusEvent,
   forwardRef,
   ReactElement,
   ReactNode,
   Ref,
   useCallback, useEffect,
   useMemo,
   useRef,
   useState
} from 'react';
import { LocalDate } from '@js-joda/core';
import {
   LocalDatePresetRange,
} from '../../../controls/pickers/localdaterangepicker';
import { InputRefType } from '../base-input/Input';
import { useCombinedRefs } from '../../../../hooks';
import { ValidatingInput } from '../validating-input/ValidatingInput';
import { DateRangePickerTrigger } from './DateRangePickerTrigger';

/**
 * {@docCategory DateRangeInput}
 */
export declare type DateRangeInputValueType = DataTypeWithInvalid<DateRange>;

/**
 * {@docCategory DateRangeInput}
 */
export interface DateRangeInputProps {

   /**
    * The name of the input item.
    */
   name?: string;

   /**
    * The value.
    */
   value?: DateRangeInputValueType;

   /**
    * The placeholder text.
    */
   placeholder?: string;

   /**
    * Set the input item to read-only.
    * @defaultvalue false
    */
   readOnly?: boolean;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * To prohibit selection/navigation before set date.
    */
   minDate?: LocalDate;

   /**
    * To prohibit selection/navigation after set date.
    */
   maxDate?: LocalDate;

   /**
    * Determines if a border should be shown around the component.
    * @defaultvalue true
    */
   border?: boolean;

   /**
    * Determines if the component should have internal padding.
    * @defaultvalue true
    */
   padding?: boolean;

   /**
    * Show the clear button. The clear button will only be shown if there is a value in the input item.
    * @defaultvalue false
    */
   allowClear?: boolean;

   /**
    * The prefix element.
    */
   prefix?: ReactNode;

   /**
    * The suffix element.
    */
   suffix?: ReactNode;

   /**
    * The label text displayed before (on the left side of) the input field.
    */
   addonBefore?: ReactNode;

   /**
    * The label text displayed after (on the right side of) the input field.
    */
   addonAfter?: ReactNode;

   /**
    * Set to hide the picker.
    *
    * @default false
    */
   hideCalendarPicker?: boolean;

   /**
    * Date range which will highlight dates in range in popup calendar.
    */
   referenceDateRange?: DateRange;

   /**
    * To add custom options which will be shown in toolbar to quickly select defined ranges in
    * popup calendar.
    * @default []
    */
   presetRanges?: LocalDatePresetRange[];

   /**
    * format cells in the popup calendar.
    */
   formatCell?: (date: LocalDate) => ReactElement | null;

   /**
    * To disable cells for date in popup calendar.
    */
   disableCell?: (date: LocalDate) => boolean;

   /**
    * Set this to focus on first render
    */
   autoFocus?: boolean;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    */
   onChange?: (value?: DateRangeInputValueType) => void;

   /**
    * Callback called when the component loses focus.
    * @param event - The focus event object
    */
   onBlur?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Callback called when the component gains focus.
    * @param event - The focus event object
    */
   onFocus?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Function called when the user presses the enter key.
    */
   onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;

   /**
    * Function called when the dirty state changes.  The input is considered dirty
    * when the raw string value that is currently typed into the field does not
    * match the last good validated value.
    *
    * @param isDirty - True if the raw string value that is currently typed into the field does not match the last good validated value
    */
   onDirtyStateChange?: (isDirty: boolean) => void;

   /**
    * Whether or not the picker should be full screen (such as for mobile devices)
    * @defaultvalue true if mobile environment is set, false otherwise
    */
   fullScreenPicker?: boolean;
}

export const DateRangeInput = forwardRef<InputRefType, DateRangeInputProps>(
   (props: DateRangeInputProps, ref: Ref<InputRefType>) => {

      const inputRef = useRef<InputRefType>(null);
      const combinedRef = useCombinedRefs<InputRefType>(inputRef, ref);

      const [value, setValue] = useState<DateRangeInputValueType | undefined>(props.value);

      const environment = getEnvironment() || {};
      const fullScreenPicker = props.fullScreenPicker === true ? true : environment.mobile;

      useEffect(()=>{
         setValue(props.value);
      }, [props.value]);

      const { onChange: propsOnChange, minDate, maxDate, disabled, readOnly, onBlur, formatCell, disableCell, referenceDateRange, presetRanges } = props;
      const onChangeHandler = useCallback((newValue?: DateRangeInputValueType) => {
         setValue(newValue);
         if (propsOnChange) {
            propsOnChange(newValue);
         }
      }, [propsOnChange]);

      const onPickerChangeHandler = useCallback((newValue?: DateRangeInputValueType) => {
         onChangeHandler(newValue);
         if (combinedRef && combinedRef.current) {
            combinedRef.current.focus();
         }
      }, [combinedRef, onChangeHandler]);

      const popupTrigger = useMemo(
         () => {
            return (<DateRangePickerTrigger
                        value={value}
                        disabled={disabled}
                        readOnly={readOnly}
                        onChange={onPickerChangeHandler}
                        minDate={minDate}
                        maxDate={maxDate}
                        formatCell={formatCell}
                        disableCell={disableCell}
                        referenceDateRange={referenceDateRange}
                        presetRanges={presetRanges}
                        fullScreen={fullScreenPicker}
            />);
         },
         [value, minDate, maxDate, disabled, readOnly, onPickerChangeHandler, formatCell, disableCell, referenceDateRange, presetRanges, fullScreenPicker ]);

      return (
         <ValidatingInput<DateRange>
            ref={combinedRef}
            name={props.name}
            type={'text'}
            inputMode={'text'}
            placeholder={props.placeholder}
            readOnly={readOnly}
            disabled={disabled}
            allowClear={props.allowClear}
            width={props.width}
            minWidth={props.minWidth}
            maxWidth={props.maxWidth}
            border={props.border}
            padding={props.padding}
            prefix={props.prefix}
            suffix={props.suffix}
            addonBefore={props.addonBefore}
            addonAfter={props.addonAfter}
            popupTrigger={props.hideCalendarPicker ? undefined : popupTrigger}
            format={dateRangeInputFormatter()}
            parse={dateRangeInputParser(minDate, maxDate)}
            inputValidator={dateRangeInputValidator()}
            className={props.className}
            style={props.style}
            value={value}
            autoFocus={props.autoFocus}
            onChange={onChangeHandler}
            onBlur={onBlur}
            onFocus={props.onFocus}
            onPressEnter={props.onPressEnter}
            onDirtyStateChange={props.onDirtyStateChange}
         />
      );
   }
);

const MAIN_SPLITTER = ' - ';

export const dateRangeInputFormatter = () => (value?: DateRange): string => {
   if (value === undefined) {
      return '';
   }

   return value ? `${formatDateYearLong(value.startDate)}${MAIN_SPLITTER}${formatDateYearLong(value.endDate)}` : '';
};

export const dateRangeInputParser = (minDate: LocalDate | undefined, maxDate: LocalDate | undefined) => {

   return (value?: string): DateRangeInputValueType => {

      if (value === undefined || value.trim().length === 0) {
         return undefined;
      }

      try {
         const regexBasedOnDashesAsDateSeparators = value.match(/[/.]/g) ? /[ ]*[-]*[ -]/
                                                                         : /[ ]*[-]*[ ]/;
         const dateRangeAsArray = value.split(regexBasedOnDashesAsDateSeparators).filter(value => value.trim() !== '');
         const startDate = parseDate(dateRangeAsArray[0]);
         const endDate = parseDate(dateRangeAsArray[1]);

         if (minDateInvalidated(startDate, minDate) ||
             maxDateInvalidated(endDate, maxDate)) {
            return InvalidValue;
         }

         return startDate.isAfter(endDate) ? InvalidValue : new DateRange(startDate, endDate);
      }
      catch (error) {
         return InvalidValue;
      }
   };
};

export const dateRangeInputValidator = () => {

   const regExp = /^\d{1,4}([./-]\d{0,2}([./-]\d{0,4})?)?\s*-?\s*(\d{0,4}([./-]\d{0,2}([./-]\d{0,4})?)?)?$/;

   return (value?: string): boolean => {
      if (value) {
         return regExp.test(value);
      }

      return true;
   };
};


const minDateInvalidated = (date?: LocalDate, minDate?: LocalDate) => {
   return minDate && date && date.isBefore(minDate);
};

const maxDateInvalidated = (date?: LocalDate, maxDate?: LocalDate) => {
   return maxDate && date && date.isAfter(maxDate);
};
